import { useCallback, useEffect, useRef, useState } from "react";

/** The GLB, turnable, wherever it is being looked at.
 *
 *  Both sides need this now and they need it to behave identically: the client
 *  turning the thing they are deciding whether to buy, and the studio turning
 *  the same thing before deciding whether to send it. A sculpt that reads badly
 *  from behind should be caught here rather than by the customer.
 *
 *  model-viewer itself is a megabyte and is imported by whoever renders this,
 *  not by this file, so neither bundle carries it until someone asks. */
export default function Model3D({
  src,
  poster,
  loadingLabel,
  hint,
  onReady,
  onFailed,
}: {
  src: string;
  /** Held on screen underneath while the model downloads. */
  poster?: string;
  loadingLabel: string;
  /** Said once there is something to turn. Nothing, where it would be noise. */
  hint?: string;
  onReady?: () => void;
  /** The GLB never arrived. There was no such path: a model that 404s or dies
   *  on a flaky connection left the card spinning for ever, with the picture
   *  the client came to see already replaced. */
  onFailed?: () => void;
}) {
  /** 0 to 1 while it downloads, then null once it is on screen. A GLB is tens
   *  of megabytes: without this the card went blank and stayed blank, and on a
   *  phone on cellular that reads as broken rather than as busy. */
  const [progress, setProgress] = useState<number | null>(0);

  /* Held in refs rather than closed over, so the element's listeners never have
     to be taken down and put back when whoever renders this re-renders with new
     inline callbacks. They used to be: the ref below depended on onFailed, the
     client page passes a fresh one on every render, and each re-render armed a
     new 30-second stall timer on a model that had long since loaded and would
     never report progress again. Switching tabs, tapping their own photo or
     scrolling past the order button tore down a model somebody was turning,
     with a toast saying it had not loaded. */
  const readyRef = useRef(onReady);
  const failedRef = useRef(onFailed);
  useEffect(() => {
    readyRef.current = onReady;
    failedRef.current = onFailed;
  });

  /** model-viewer reports its download through custom events, which React
   *  cannot attach as props: `onLoad` in particular is a name React keeps for
   *  itself and never binds on a custom element, so a ready callback passed that
   *  way simply never ran. The element is caught on mount instead, once, and the
   *  cleanup React 19 calls on unmount takes the timer and the listeners with
   *  it, so a stall armed before "Back to the picture" cannot fire afterwards. */
  const attach = useCallback((node: HTMLElement | null) => {
    if (!node) return;

    // Nothing at all for this long and it is not slow, it is broken. Generous
    // on purpose: a big model on a bad connection is allowed to take its time,
    // and the bar keeps moving while it does. Any progress at all resets it,
    // and once the model is on screen nothing arms it again.
    const STALL_MS = 30_000;
    let loaded = false;
    let failed = false;
    let timer: number | undefined;
    const fail = () => {
      window.clearTimeout(timer);
      if (failed) return;
      failed = true;
      failedRef.current?.();
    };
    const keepWaiting = () => {
      window.clearTimeout(timer);
      if (!loaded) timer = window.setTimeout(() => !loaded && fail(), STALL_MS);
    };

    const onProgress = (event: Event) => {
      const total = (event as CustomEvent<{ totalProgress?: number }>).detail?.totalProgress;
      if (typeof total !== "number") return;
      keepWaiting();
      setProgress(total >= 1 ? null : total);
    };
    // Belt and braces for the bar: a model served from cache can finish before
    // the first progress event lands, and then the bar would never clear.
    const onLoad = () => {
      window.clearTimeout(timer);
      setProgress(null);
      if (loaded) return;
      loaded = true;
      readyRef.current?.();
    };

    node.addEventListener("progress", onProgress);
    node.addEventListener("load", onLoad);
    node.addEventListener("error", fail);
    keepWaiting();
    if ((node as HTMLElement & { loaded?: boolean }).loaded) onLoad();

    return () => {
      window.clearTimeout(timer);
      node.removeEventListener("progress", onProgress);
      node.removeEventListener("load", onLoad);
      node.removeEventListener("error", fail);
    };
  }, []);

  return (
    <div className="viewer-frame">
      <model-viewer
        ref={attach}
        class="viewer"
        src={src}
        alt=""
        poster={poster}
        camera-controls
        auto-rotate
        touch-action="pan-y"
        shadow-intensity="1"
        exposure="1.05"
      />

      {progress !== null ? (
        <div className="viewer-loading">
          <div className="spin" />
          <span className="tiny">{loadingLabel}</span>
          <div className="bar viewer-bar">
            <span style={{ width: `${Math.max(6, Math.round(progress * 100))}%` }} />
          </div>
        </div>
      ) : (
        hint && <p className="viewer-hint tiny">{hint}</p>
      )}
    </div>
  );
}
